/**
 * 設定 → 工程表の休み／工程表の雛形（#101、docs/39 §4.1 の12・13、§4.2）。工程管理のオプションが使える会社だけメニューに出す。
 *   工程表の休み（管理者）：休みの曜日・祝日（新しい工程表の既定）と、会社の休み（年末年始・お盆などの期間＝全部の工程表に効く）。
 *     保存すると API が同じトランザクションでその会社の工程表を全部計算し直す（開いている工程表は「ほかの人が直しました」で読み直す）
 *   工程表の雛形（だれでも）：一覧・名前変更・削除。作るのは工程表の「⋮」→「雛形として保存」
 */
import { SCHEDULE_LIMITS, WEEKDAY_JA, spanText, type CompanyScheduleCalendar, type ScheduleTemplateItem } from "@genbaryoku/contract";
import { SCHEDULE_MSG } from "./schedule/msg.js";
import { t as tx } from "../labels.js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, messageOf } from "../api.js";
import { useMe } from "../auth.js";
import { BackButton, differs, useBackNav, usePageName } from "../components/BackButton.jsx";
import { CrIcon } from "../components/Create.jsx";
import { ErrorText, Field, Help, Switch, guardComposingEnter, useToast } from "../components/ui.jsx";
import { md } from "../lib/num.js";
import { DateButton, useEsc } from "./schedule/Dialogs.jsx";
import { todayLocal } from "./schedule/timeline.js";

type OffRow = { key: string; from: string; to: string; name: string };
let seq = 0;
const keyOf = () => `o${++seq}`;

export function SettingsScheduleCalendarPage() {
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const available = !!me.data?.features.schedule;
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const q = useQuery({ queryKey: ["schedule-calendar"], queryFn: () => api<CompanyScheduleCalendar>("GET", "/company/schedule-calendar"), enabled: available });
  // 進捗を％で入力（#292、docs/102 §4 ★5）：スイッチ＝押した瞬間に保存（休みの「保存」とは別。工程表の日付は変わらない）
  const [pctErr, setPctErr] = useState<string | undefined>();
  const pctSave = useMutation({
    mutationFn: (on: boolean) => api<CompanyScheduleCalendar>("PUT", "/company/schedule-progress", { progressPct: on }),
    onSuccess: (c) => {
      qc.setQueryData(["schedule-calendar"], (old: CompanyScheduleCalendar | undefined) => (old ? { ...old, progressPct: c.progressPct } : c));
      void qc.invalidateQueries({ queryKey: ["schedule"] });
      void qc.invalidateQueries({ queryKey: ["schedule-overview"] });
      void qc.invalidateQueries({ queryKey: ["group"] });
      setPctErr(undefined);
      toast(c.progressPct ? SCHEDULE_MSG.progressPctOn : SCHEDULE_MSG.progressPctOff);
    },
    onError: (e) => setPctErr(messageOf(e)),
  });
  const [form, setForm] = useState<{ offWeekdays: number[]; holidayOff: boolean; offDays: OffRow[] } | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm({ offWeekdays: q.data.offWeekdays, holidayOff: q.data.holidayOff, offDays: q.data.offDays.map((o) => ({ ...o, key: keyOf() })) });
  }, [q.data, form]);
  const plain = (f: NonNullable<typeof form>) => ({ offWeekdays: f.offWeekdays, holidayOff: f.holidayOff, offDays: f.offDays.map(({ from, to, name }) => ({ from, to, name })) });
  const back = useBackNav({ to: "/settings", label: "設定" }, !!form && !!q.data && differs(plain(form), q.data));
  usePageName(SCHEDULE_MSG.calendarTitle);
  const [err, setErr] = useState<string | undefined>();
  const save = useMutation({
    mutationFn: (v: CompanyScheduleCalendar) => api<CompanyScheduleCalendar>("PUT", "/company/schedule-calendar", v),
    onSuccess: (c) => {
      qc.setQueryData(["schedule-calendar"], c);
      setForm({ offWeekdays: c.offWeekdays, holidayOff: c.holidayOff, offDays: c.offDays.map((o) => ({ ...o, key: keyOf() })) });
      void qc.invalidateQueries({ queryKey: ["schedule"] });
      void qc.invalidateQueries({ queryKey: ["group"] });
      setErr(undefined);
      toast(SCHEDULE_MSG.calendarSaved);
    },
    onError: (e) => setErr(messageOf(e)),
  });

  if (!available) return <p className="soonbox">{SCHEDULE_MSG.optionOff}</p>;
  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!form) return <p className="hint">読み込み中…</p>;
  const off = new Set(form.offWeekdays);
  const setOff = (i: number, patch: Partial<OffRow>) => setForm((f) => (f ? { ...f, offDays: f.offDays.map((o, k) => (k === i ? { ...o, ...patch } : o)) } : f));
  const submit = () => {
    for (const o of form.offDays) {
      if (o.to < o.from) return setErr(`${spanText(o.from, o.to)}：${SCHEDULE_MSG.offDayOrder}`);
      if ((Date.parse(o.to) - Date.parse(o.from)) / 86_400_000 + 1 > SCHEDULE_LIMITS.offDaySpan) return setErr(`${spanText(o.from, o.to)}：${SCHEDULE_MSG.offDaySpanOver}`);
    }
    save.mutate(plain(form));
  };

  return (
    <>
      <BackButton back={back} />
      {back.dialog}
      <div className="hrow">
        <h1>{SCHEDULE_MSG.calendarTitle}</h1>
        <span className="sp" />
      </div>
      {!admin ? <p className="readonly-note">変更できるのは管理者のみです。</p> : null}
      <form
        className="paper sch-setcal"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (admin) submit();
        }}
      >
        <h2>
          休日の曜日・祝日
          <Help testId="help-schedule-calendar" text={tx("新規の{schedule}の休日の既定です。作成済の{schedule}の休日の曜日・祝日は、各{schedule}の「⋮」→「休日（…）」で変更します。休日は{task}の日数に含みません。")} />
        </h2>
        <fieldset className="wds" disabled={!admin} aria-label="休日の曜日">
          {WEEKDAY_JA.map((w, k) => (
            <label key={w} className="ck">
              <input
                type="checkbox"
                checked={off.has(k)}
                disabled={!off.has(k) && off.size >= 6}
                onChange={(e) => {
                  const next = new Set(off);
                  if (e.target.checked) next.add(k);
                  else next.delete(k);
                  setForm({ ...form, offWeekdays: [...next].sort() });
                }}
                data-testid={`sch-cal-wd-${k}`}
              />
              {w}曜日
            </label>
          ))}
        </fieldset>
        <label className="ck">
          <input type="checkbox" checked={form.holidayOff} disabled={!admin} onChange={(e) => setForm({ ...form, holidayOff: e.target.checked })} data-testid="sch-cal-holiday" />
          祝日も休日
        </label>

        <h2>
          {SCHEDULE_MSG.offDaysTitle}
          <Help testId="help-schedule-offdays" text={tx("年末年始・お盆など、会社一斉の休業期間です。作成済のすべての{schedule}の休日になります（各{schedule}の「⋮」→「休日（…）」で除外できます）。保存すると、作成済の{schedule}の終了日を再計算します。")} />
        </h2>
        {form.offDays.length ? null : <p className="hint" style={{ margin: 0 }}>会社の休みはありません。</p>}
        {form.offDays.map((o, i) => (
          <div key={o.key} className="sch-offrow" data-testid="sch-cal-offday">
            <DateButton value={o.from} onChange={(v) => setOff(i, { from: v, to: o.to < v ? v : o.to })} label="から" testId={`sch-cal-from-${i}`} />
            <DateButton value={o.to} onChange={(v) => setOff(i, { to: v })} label="まで" testId={`sch-cal-to-${i}`} />
            <Field label={SCHEDULE_MSG.offDayName} id={`sch-cal-name-${o.key}`}>
              <input
                id={`sch-cal-name-${o.key}`}
                className="inp"
                value={o.name}
                maxLength={SCHEDULE_LIMITS.offDayName}
                disabled={!admin}
                onChange={(e) => setOff(i, { name: e.target.value })}
                onKeyDown={guardComposingEnter}
                autoComplete="off"
              />
            </Field>
            {admin ? (
              <button type="button" className="btn text danger" aria-label={`${spanText(o.from, o.to)} の休みを削除`} onClick={() => setForm({ ...form, offDays: form.offDays.filter((_, k) => k !== i) })}>
                削除
              </button>
            ) : null}
          </div>
        ))}
        {admin ? (
          <button
            type="button"
            className="btn text"
            style={{ alignSelf: "flex-start" }}
            disabled={form.offDays.length >= SCHEDULE_LIMITS.companyOffDays}
            onClick={() => {
              const t = todayLocal();
              setForm({ ...form, offDays: [...form.offDays, { key: keyOf(), from: t, to: t, name: "" }] });
            }}
            data-testid="sch-cal-add"
          >
            <CrIcon k="plus" />
            {SCHEDULE_MSG.offDayAdd}
          </button>
        ) : null}
        <ErrorText>{err}</ErrorText>
        {admin ? (
          <div>
            <button type="submit" className="btn primary" disabled={save.isPending} data-testid="sch-cal-save">
              保存
            </button>
          </div>
        ) : null}
      </form>
      <section className="paper sch-setcal" aria-labelledby="sch-set-progress" data-testid="sch-set-progress">
        <h2 id="sch-set-progress">
          {SCHEDULE_MSG.progressSection}
          <Help testId="help-schedule-progress" text={tx(SCHEDULE_MSG.progressPctHelp)} />
        </h2>
        <Switch
          checked={(pctSave.isPending ? pctSave.variables : q.data?.progressPct) ?? false}
          disabled={!admin || pctSave.isPending}
          onChange={(v) => pctSave.mutate(v)}
          label={SCHEDULE_MSG.progressPctSwitch}
          testId="sch-set-progress-pct"
        />
        <ErrorText>{pctErr}</ErrorText>
      </section>
      {node}
    </>
  );
}

export function SettingsScheduleTemplatesPage() {
  const me = useMe();
  const available = !!me.data?.features.schedule;
  const qc = useQueryClient();
  const { toast, node } = useToast();
  const q = useQuery({ queryKey: ["schedule-templates"], queryFn: () => api<ScheduleTemplateItem[]>("GET", "/schedule-templates"), enabled: available });
  const back = useBackNav({ to: "/settings", label: "設定" });
  usePageName(tx("{schedule}のテンプレート"), tx("{schedule}テンプレート")); // 戻るボタンは「を」「の」なし（#240）
  const [rename, setRename] = useState<ScheduleTemplateItem | null>(null);
  const [del, setDel] = useState<ScheduleTemplateItem | null>(null);
  const remove = useMutation({
    mutationFn: (t: ScheduleTemplateItem) => api<void>("DELETE", `/schedule-templates/${t.id}`),
    onSuccess: (_, t) => {
      setDel(null);
      void qc.invalidateQueries({ queryKey: ["schedule-templates"] });
      toast(SCHEDULE_MSG.templateDeleted(t.name));
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  // 標準テンプレート（#291）：複製で自社のテンプレートに・非表示（空の{schedule}の「テンプレートから」に出さない）
  const copy = useMutation({
    mutationFn: (t: ScheduleTemplateItem) => api<ScheduleTemplateItem>("POST", `/schedule-templates/${t.id}/copy`),
    onSuccess: (x) => {
      void qc.invalidateQueries({ queryKey: ["schedule-templates"] });
      toast(SCHEDULE_MSG.templateCopied(x.name));
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  const hide = useMutation({
    mutationFn: (t: ScheduleTemplateItem) => api<ScheduleTemplateItem>("PUT", `/schedule-templates/${t.id}/hidden`, { hidden: !t.hidden }),
    onSuccess: (x) => {
      void qc.invalidateQueries({ queryKey: ["schedule-templates"] });
      toast(x.hidden ? SCHEDULE_MSG.templateHidden(x.name) : SCHEDULE_MSG.templateShown(x.name));
    },
    onError: (e) => toast(messageOf(e), undefined, "error"),
  });
  if (!available) return <p className="soonbox">{SCHEDULE_MSG.optionOff}</p>;
  const items = q.data ?? [];
  const own = items.filter((t) => !t.builtin).length;
  return (
    <>
      <BackButton back={back} />
      <div className="hrow">
        <h1>{tx("{schedule}のテンプレート")}</h1>
        <Help testId="help-schedule-templates" text={SCHEDULE_MSG.templatesHelp} />
        <span className="sp" />
      </div>
      {q.data ? (
        <p className="hint" style={{ margin: "0 0 10px" }}>
          {own}件 ／ 上限 {SCHEDULE_LIMITS.templates}件
        </p>
      ) : null}
      {q.isError ? <ErrorText>{messageOf(q.error)}</ErrorText> : null}
      {q.data && !items.length ? <p className="paper" style={{ padding: "18px 22px", maxWidth: 760 }}>{SCHEDULE_MSG.templatesEmpty}</p> : null}
      {items.length ? (
        <div className="tbl-wrap">
          <table className="list" data-testid="sch-templates">
            <thead>
              <tr>
                <th>名前</th>
                <th>行数</th>
                <th>更新日</th>
                <th>作成者</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {items.map((t) => (
                <tr key={t.id} data-testid="sch-template-row" data-builtin={t.builtin ? "1" : "0"} className={t.hidden ? "is-off" : undefined}>
                  <td>
                    <b>{t.name}</b>
                    {t.builtin ? (
                      <span className="chip" style={{ marginLeft: 8 }} data-testid="sch-template-builtin">
                        {SCHEDULE_MSG.builtin}
                      </span>
                    ) : null}
                    {t.hidden ? (
                      <span className="chip" style={{ marginLeft: 6 }} data-testid="sch-template-hidden">
                        {SCHEDULE_MSG.templateHide}
                      </span>
                    ) : null}
                  </td>
                  <td className="num">{t.rowCount}</td>
                  <td>{t.builtin ? "" : md(t.updatedAt.slice(0, 10))}</td>
                  <td>{t.createdBy}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button type="button" className="btn text" disabled={copy.isPending} onClick={() => copy.mutate(t)} aria-label={`「${t.name}」を複製`} data-testid="sch-template-copy">
                      {SCHEDULE_MSG.templateCopy}
                    </button>
                    {t.builtin ? (
                      <button type="button" className="btn text" disabled={hide.isPending} onClick={() => hide.mutate(t)} aria-label={`「${t.name}」を${t.hidden ? SCHEDULE_MSG.templateShow : SCHEDULE_MSG.templateHide}`} data-testid="sch-template-hide">
                        {t.hidden ? SCHEDULE_MSG.templateShow : SCHEDULE_MSG.templateHide}
                      </button>
                    ) : (
                      <>
                        <button type="button" className="btn text" onClick={() => setRename(t)} aria-label={`「${t.name}」の名前変更`}>
                          名前変更
                        </button>
                        <button type="button" className="btn text danger" onClick={() => setDel(t)} aria-label={`「${t.name}」を削除`}>
                          削除
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      {rename ? (
        <RenameDialog
          t={rename}
          onCancel={() => setRename(null)}
          onDone={() => {
            setRename(null);
            void qc.invalidateQueries({ queryKey: ["schedule-templates"] });
            toast(SCHEDULE_MSG.templateRenamed);
          }}
        />
      ) : null}
      {del ? <DeleteDialog t={del} busy={remove.isPending} onCancel={() => setDel(null)} onDelete={() => remove.mutate(del)} /> : null}
      {node}
    </>
  );
}

function RenameDialog({ t, onCancel, onDone }: { t: ScheduleTemplateItem; onCancel: () => void; onDone: () => void }) {
  const [name, setName] = useState(t.name);
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  useEsc(onCancel);
  const submit = async () => {
    if (!name.trim()) return setErr("名前を入力してください");
    setBusy(true);
    try {
      await api("PUT", `/schedule-templates/${t.id}`, { name: name.trim() });
      onDone();
    } catch (e) {
      setErr(messageOf(e));
      setBusy(false);
    }
  };
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg" role="dialog" aria-label="テンプレート名変更" data-testid="sch-template-rename">
        <div className="dh">テンプレート名変更</div>
        <div className="db">
          <Field label={SCHEDULE_MSG.templateName} id="sch-tpl-name">
            <input
              id="sch-tpl-name"
              className="inp"
              value={name}
              maxLength={SCHEDULE_LIMITS.templateName}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                guardComposingEnter(e);
                if (e.key === "Enter" && !e.defaultPrevented) void submit();
              }}
            />
          </Field>
          <ErrorText>{err}</ErrorText>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onCancel}>
            取消
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={submit}>
            名前保存
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteDialog({ t, busy, onCancel, onDelete }: { t: ScheduleTemplateItem; busy: boolean; onCancel: () => void; onDelete: () => void }) {
  useEsc(onCancel);
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg" role="alertdialog" aria-label="テンプレート削除" data-testid="sch-template-delete">
        <div className="dh">テンプレート「{t.name}」を削除</div>
        <div className="db">
          <p style={{ margin: 0 }}>{tx("作成済の{schedule}は変わりません。")}</p>
        </div>
        <div className="df">
          <button type="button" className="btn" onClick={onCancel} autoFocus>
            取消
          </button>
          <button type="button" className="btn shu" disabled={busy} onClick={onDelete}>
            削除
          </button>
        </div>
      </div>
    </div>
  );
}
